import { ApiService } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import { ApiError } from '../types/api';
import {
  AdminNotificationSettings,
  AdminProfile,
  AdminProfileBranch,
  ChangeAdminPasswordRequest,
  UpdateAdminNotificationsRequest,
  UpdateAdminProfilePictureRequest,
  UpdateAdminProfileRequest,
} from '../types/profile';

type RawProfile = Record<string, unknown>;

const toStringValue = (value: unknown, fallback = ''): string =>
  value === undefined || value === null ? fallback : String(value);

const normalizeBranch = (raw: unknown): AdminProfileBranch | null => {
  if (!raw || typeof raw !== 'object') return null;
  const branch = raw as Record<string, unknown>;
  const id = Number(branch.id);
  const name = toStringValue(branch.name);
  if (!Number.isFinite(id) || !name) return null;
  return { id, name };
};

const normalizeNotificationSettings = (raw: unknown): AdminNotificationSettings => {
  const source =
    raw && typeof raw === 'object'
      ? (raw as Record<string, unknown>)
      : {};

  return {
    emailAlerts: Boolean(source.emailAlerts ?? source.email_alerts ?? true),
    taskReminders: Boolean(source.taskReminders ?? source.task_reminders ?? true),
  };
};

export const normalizeAdminProfile = (raw: RawProfile): AdminProfile => ({
  fullName: toStringValue(raw.fullName ?? raw.full_name),
  email: toStringValue(raw.email),
  phoneNumber: toStringValue(raw.phoneNumber ?? raw.phone_number),
  branch: normalizeBranch(raw.branch),
  profilePictureUrl: (raw.profilePictureUrl ?? raw.profile_picture_url ?? null) as string | null,
  passwordChangedAt: (raw.passwordChangedAt ?? raw.password_changed_at ?? null) as string | null,
  notificationSettings: normalizeNotificationSettings(
    raw.notificationSettings ?? raw.notification_settings
  ),
});

export const formatPasswordStatusText = (passwordChangedAt: string | null): string => {
  if (!passwordChangedAt) {
    return 'Password has not been changed yet. We recommend updating every 6 months.';
  }

  const changedAt = new Date(passwordChangedAt);
  if (Number.isNaN(changedAt.getTime())) {
    return 'We recommend updating your password every 6 months.';
  }

  const months = Math.floor((Date.now() - changedAt.getTime()) / (1000 * 60 * 60 * 24 * 30));
  if (months <= 0) {
    return 'Last changed recently. We recommend updating every 6 months.';
  }
  if (months === 1) {
    return 'Last changed 1 month ago. We recommend updating every 6 months.';
  }
  return `Last changed ${months} months ago. We recommend updating every 6 months.`;
};

export const getProfileErrorMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as ApiError).message;
    if (typeof message === 'string' && message.trim()) {
      return message;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

export class ProfileService extends ApiService {
  async getProfile(): Promise<AdminProfile> {
    const response = await this.get<RawProfile>(API_ENDPOINTS.profile.root);
    return normalizeAdminProfile(response);
  }

  async updateProfile(data: UpdateAdminProfileRequest): Promise<AdminProfile> {
    const response = await this.patch<RawProfile>(API_ENDPOINTS.profile.root, data);
    return normalizeAdminProfile(response);
  }

  async updateProfilePicture(data: UpdateAdminProfilePictureRequest): Promise<void> {
    await this.put<void>(API_ENDPOINTS.profile.picture, data);
  }

  async removeProfilePicture(): Promise<void> {
    await this.delete<void>(API_ENDPOINTS.profile.picture);
  }

  async getNotificationPreferences(): Promise<AdminNotificationSettings> {
    const response = await this.get<RawProfile>(API_ENDPOINTS.profile.notifications);
    return normalizeNotificationSettings(response);
  }

  async updateNotificationPreferences(
    data: UpdateAdminNotificationsRequest
  ): Promise<AdminNotificationSettings> {
    const response = await this.patch<RawProfile>(API_ENDPOINTS.profile.notifications, data);
    return normalizeNotificationSettings(response);
  }

  async changePassword(data: ChangeAdminPasswordRequest): Promise<void> {
    await this.put<void>(API_ENDPOINTS.profile.password, data);
  }
}

export const profileService = new ProfileService();
